import { useEffect, useMemo, useState } from "react"
import { INewReplenishmentResponse } from "interfaces/IReplenishment"
import { useNavigate } from "react-router"
import { Header, InfoText, StrongText } from "./styles"
import { Box, debounce, MenuItem, Select, Typography, CircularProgress } from "@mui/material"
import { toast } from "react-toastify"
import { replenishmentService } from "services/replenishmentService"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { configsService } from "services/configsService"
import { IConfigDataResponse } from "interfaces/IConfig"
import { productService } from "services/productService"
import { IProductData, IProductResponse, IBatch } from "interfaces/IProduct"
import { BatchResponse, batchService } from "services/batchService"
import Cam from "utils/Cam"

const EspecificSkuReplenishing = () => {
  const [successFeedback, setSuccessFeedback] = useState(false)
  const [isLoading, setisLoading] = useState(false)
  const [isConfigLoading, setIsConfigLoading] = useState(true)
  const [isLoadingBatches, setIsLoadingBatches] = useState(false)
  const [marketReceiving, setMarketReceiving] = useState<number | "">("")
  const [batches, setBatches] = useState<number[]>([])
  const [newReplenishment, setNewReplenishment] = useState<INewReplenishmentResponse | undefined>(
    undefined,
  )
  const [config, setConfig] = useState<IConfigDataResponse>({
    id: 0,
    created: "",
    totalMarkets: 0,
    modifiedById: 0,
    modifiedByUsername: "",
    useScanner: false,
  })
  const [products, setProducts] = useState<IProductResponse>({
    page: 0,
    page_size: 0,
    results: [],
    total_items: 0,
    total_pages: 0,
  })
  const [inputQuantityValue, setInputQuantityValue] = useState("")
  const [inputSkuValue, setInputSkuValue] = useState("")
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)
  const [selectedSku, setSelectedSku] = useState<IProductData | null>(null)
  const [filterText, setFilterText] = useState("")
  const [selectedBatch, setSelectedBatch] = useState<number | "">("")
  const [showScanner, setShowScanner] = useState(true)
  const disableConfirmBotton =
    !inputQuantityValue ||
    !inputSkuValue ||
    !selectedSku ||
    !marketReceiving ||
    !selectedBatch ||
    isLoading ||
    Number(inputQuantityValue) < 1 ||
    isNaN(Number(inputQuantityValue))

  const navigate = useNavigate()

  const handleInputSkuChange = (event: any) => {
    setInputSkuValue(event.target.value)
    debouncedSearch(event.target.value)
  }

  const handleInputQuantityChange = (event: any) => {
    setInputQuantityValue(event.target.value)
  }

  const handleReceivingMarketNumberChange = (event: any) => {
    setMarketReceiving(event.target.value)
  }

  const handleCreateReplenishment = async () => {
    try {
      setisLoading(true)
      const data = {
        marketNumber: marketReceiving === "" ? 0 : marketReceiving,
        productId: selectedSku?.productId,
        quantity: Number(inputQuantityValue),
        batchId: selectedBatch,
      }
      await replenishmentService.createReplenishment(data)
      setSuccessFeedback(true)
    } catch (error: any) {
      toast(error?.message ?? "Ocorreu um erro, dá um alô pro time de tech.", { type: "error" })
    } finally {
      setisLoading(false)
    }
  }

  const handleGetConfigs = async () => {
    try {
      setIsConfigLoading(true)
      const response_config = await configsService.getConfig()
      setConfig(response_config)
    } catch (error: any) {
      toast(
        error?.message ?? "Ocorreu um erro ao buscar as configurações, dá um alô pro time de tech.",
        { type: "error" },
      )
    } finally {
      setIsConfigLoading(false)
    }
  }

  const handleGetProducts = async () => {
    try {
      setisLoading(true)
      const response_products = await productService.getProducts({
        orderBy: "name",
        status: "active",
      })
      setProducts(response_products)
    } catch (error: any) {
      toast(error?.message ?? "Ocorreu um erro ao buscar skus, dá um alô pro time de tech.", {
        type: "error",
      })
    } finally {
      setisLoading(false)
    }
  }

  const handleNewReplenish = () => {
    setisLoading(true)
    setSuccessFeedback(false)
    navigate("/painel-de-operador/abastecimento")
    setisLoading(false)
  }

  const handleFocus = () => {
    setIsDropdownVisible(true)
    setInputSkuValue("")
    setFilterText("")
    setSelectedSku(null)
  }

  const handleSelectProduct = async (product: IProductData) => {
    setSelectedSku(product)
    setFilterText(product.name)
    setInputSkuValue(product.name)
    setIsDropdownVisible(false)
    setIsLoadingBatches(true)
    try {
      const response = await batchService.getAllBaseProducts({
        productBaseId: product.productBaseId,
      })
      const batchIds = response.results[0]?.Batchs?.map(batch => batch.batchId) || []
      setBatches(batchIds)
    } catch (error: any) {
      toast(error?.message ?? "Ocorreu um erro ao buscar os lotes, dá um alô pro time de tech.", {
        type: "error",
      })
    } finally {
      setIsLoadingBatches(false)
    }
  }

  const handleScanResult = async (code: string) => {
    try {
      setisLoading(true)
      const response = await productService.checkBatch(code, "")
      setSelectedSku(response.skuData)
      setSelectedBatch(response.batch_id)
      setInputSkuValue(response.skuData.name)
      setShowScanner(false)
    } catch (error: any) {
      toast(error?.message ?? "Ocorreu um erro ao buscar o sku, dá um alô pro time de tech.", {
        type: "error",
      })
    } finally {
      setisLoading(false)
    }
  }

  const handleScanAgain = () => {
    setShowScanner(true)
    setSelectedSku(null)
    setSelectedBatch("")
    setInputQuantityValue("")
    setMarketReceiving(0)
    setInputSkuValue("")
  }

  const handleBatchChange = (event: any) => {
    setSelectedBatch(event.target.value)
  }

  const generateNumberList =
    config.totalMarkets > 0 ? Array.from({ length: config.totalMarkets }, (_, i) => i + 1) : []

  const handleGetProductDebounce = async (skuFiltered: string) => {
    try {
      setisLoading(true)
      const response_products = await productService.getProducts({
        orderBy: "name",
        status: "active",
        skuName: skuFiltered,
        pageSize: 1000,
      })
      setProducts(response_products)
    } catch (error: any) {
      toast(error?.message ?? "Ocorreu um erro ao buscar skus, dá um alô pro time de tech.", {
        type: "error",
      })
    } finally {
      setisLoading(false)
    }
  }

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        handleGetProductDebounce(searchTerm)
      }, 1000),
    [],
  )

  useEffect(() => {
    handleGetConfigs()
    handleGetProducts()
  }, [])

  return (
    <Box>
      {successFeedback ? (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          marginTop="50px"
        >
          <CheckCircleIcon sx={{ fontSize: 100, color: "#FF8C42" }} />
          <Typography
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: "500",
              fontSize: "21px",
              lineHeight: "33.6px",
              letterSpacing: "1%",
              marginTop: "20px",
            }}
          >
            Produto abastecido! 👍
          </Typography>
          <button
            onClick={handleNewReplenish}
            disabled={isLoading}
            style={{
              marginTop: "40px",
              padding: "10px 20px",
              fontSize: "18px",
              backgroundColor: "#FF8C42",
              color: "white",
              border: "none",
              borderRadius: "16px",
              cursor: "pointer",
              width: "300px",
            }}
          >
            beleza, valeu!
          </button>
        </Box>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {isConfigLoading ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              marginTop="50px"
            >
              <CircularProgress sx={{ color: "#FF8C42" }} />
              <Typography
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: "500",
                  fontSize: "21px",
                  lineHeight: "33.6px",
                  letterSpacing: "1%",
                  marginTop: "20px",
                }}
              >
                Carregando configurações...
              </Typography>
            </Box>
          ) : (
            <>
              {config.useScanner && (
                <Box
                  style={{
                    width: "100%",
                    padding: "20px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "8px",
                    marginBottom: "20px",
                  }}
                >
                  <Box>
                    <InfoText>
                      Produto: <StrongText>{selectedSku?.name || "Ainda não definido"}</StrongText>
                    </InfoText>
                    <InfoText>
                      Porcionamento:{" "}
                      <StrongText>
                        {selectedSku?.purchaseValue}{" "}
                        {selectedSku?.portionType === "peso"
                          ? "kg"
                          : selectedSku?.portionType === "unidade"
                            ? "un"
                            : "Ainda não definido"}
                      </StrongText>
                    </InfoText>
                    <InfoText>
                      Etapa:{" "}
                      <StrongText>
                        {selectedSku?.separationOrder || "Ainda não definido"}
                      </StrongText>
                    </InfoText>
                    <InfoText>
                      Lote: <StrongText>{selectedBatch || "Ainda não definido"}</StrongText>
                    </InfoText>
                  </Box>
                </Box>
              )}
              <Typography
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: "500",
                  fontSize: "27px",
                  lineHeight: "33.6px",
                  letterSpacing: "1%",
                  marginBottom: "1rem",
                }}
              >
                Sku específico
              </Typography>
              {config.useScanner ? (
                <>
                  {showScanner ? (
                    <div style={{ marginBottom: "20px", textAlign: "center" }}>
                      <Typography
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: "500",
                          fontSize: "20px",
                          lineHeight: "33.6px",
                          letterSpacing: "1%",
                          marginBottom: "1rem",
                        }}
                      >
                        Escaneie o código QR do SKU
                      </Typography>
                      <Cam onSubmit={handleScanResult} loading={isLoading} />
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: "20px", textAlign: "center" }}>
                        <button
                          onClick={handleScanAgain}
                          style={{
                            padding: "8px 16px",
                            fontSize: "16px",
                            backgroundColor: "#FF8C42",
                            color: "white",
                            border: "none",
                            borderRadius: "16px",
                            cursor: "pointer",
                            width: "200px",
                            marginBottom: "20px",
                          }}
                        >
                          Escanear outro SKU
                        </button>
                      </div>
                      <Typography
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: "500",
                          fontSize: "20px",
                          lineHeight: "33.6px",
                          letterSpacing: "1%",
                        }}
                      >
                        Quantos pacotes?
                      </Typography>

                      <input
                        type="number"
                        min="1"
                        value={inputQuantityValue}
                        onChange={handleInputQuantityChange}
                        style={{
                          padding: "8px",
                          fontSize: "18px",
                          width: "260px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          textAlign: "center",
                          marginTop: "0.5rem",
                          marginBottom: "0.5rem",
                        }}
                        placeholder="Ex. 40"
                      />
                      <div>
                        <label
                          htmlFor="receivingMarketNumber"
                          style={{
                            display: "block",
                            fontSize: "18px",
                            fontWeight: "500",
                            marginBottom: "0.5rem",
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            style={{
                              fontFamily: "Inter, sans-serif",
                              fontWeight: "500",
                              fontSize: "20px",
                              lineHeight: "33.6px",
                              letterSpacing: "1%",
                              marginTop: "0.5rem",
                            }}
                          >
                            Vai abastecer qual mercado?
                          </Typography>
                        </label>
                        <div style={{ display: "flex", justifyContent: "center" }}>
                          <Select
                            id="receivingMarketNumber"
                            value={marketReceiving}
                            onChange={handleReceivingMarketNumberChange}
                            disabled={isLoading}
                            displayEmpty
                            style={{
                              fontSize: "18px",
                              width: "260px",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                              textAlign: "center",
                              height: "2.5rem",
                            }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 200,
                                  overflowY: "auto",
                                },
                              },
                            }}
                            renderValue={selected => {
                              if (!selected) {
                                return "Selecione..."
                              }
                              return `Mercado ${selected}`
                            }}
                          >
                            {generateNumberList.map(number => (
                              <MenuItem key={number} value={number}>
                                Mercado {number}
                              </MenuItem>
                            ))}
                          </Select>
                        </div>
                      </div>
                      <button
                        disabled={disableConfirmBotton}
                        onClick={handleCreateReplenishment}
                        style={{
                          marginTop: "0.5rem",
                          padding: "10px 20px",
                          fontSize: "18px",
                          backgroundColor: disableConfirmBotton ? "#FFC78F" : "#FF8C42",
                          cursor: disableConfirmBotton ? "not-allowed" : "pointer",
                          color: "white",
                          border: "none",
                          borderRadius: "16px",
                          width: "300px",
                        }}
                      >
                        Abastecer
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div style={{ marginBottom: "20px", textAlign: "center" }}>
                    <label
                      htmlFor="productSearch"
                      style={{
                        display: "block",
                        fontSize: "18px",
                        fontWeight: "500",
                        marginBottom: "8px",
                      }}
                    >
                      <Typography
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: "500",
                          fontSize: "20px",
                          lineHeight: "33.6px",
                          letterSpacing: "1%",
                        }}
                      >
                        Vai abastecer qual sku?
                      </Typography>
                    </label>
                    <input
                      id="productSearch"
                      type="text"
                      value={inputSkuValue}
                      onChange={handleInputSkuChange}
                      onFocus={handleFocus}
                      placeholder="Digite o nome e selecione"
                      disabled={isLoading}
                      style={{
                        padding: "8px",
                        fontSize: "18px",
                        width: "260px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        textAlign: "center",
                      }}
                    />
                    {isDropdownVisible && (
                      <ul
                        style={{
                          marginTop: "5px",
                          padding: "0",
                          listStyleType: "none",
                          width: "260px",
                          maxHeight: "150px",
                          overflowY: "auto",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          position: "absolute",
                          backgroundColor: "white",
                          zIndex: 1,
                        }}
                      >
                        {products.results.map(product => (
                          <li
                            key={product.productId}
                            onClick={() => handleSelectProduct(product)}
                            style={{
                              padding: "8px",
                              cursor: "pointer",
                            }}
                          >
                            {product.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <Typography
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: "500",
                      fontSize: "20px",
                      lineHeight: "33.6px",
                      letterSpacing: "1%",
                    }}
                  >
                    Quantos pacotes?
                  </Typography>

                  <input
                    type="number"
                    min="1"
                    value={inputQuantityValue}
                    onChange={handleInputQuantityChange}
                    style={{
                      padding: "8px",
                      fontSize: "18px",
                      width: "260px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      textAlign: "center",
                      marginTop: "0.5rem",
                      marginBottom: "0.5rem",
                    }}
                    placeholder="Ex. 40"
                  />
                  {selectedSku && (
                    <div>
                      <label
                        htmlFor="batchSelect"
                        style={{
                          display: "block",
                          fontSize: "18px",
                          fontWeight: "500",
                          marginBottom: "0.5rem",
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontWeight: "500",
                            fontSize: "20px",
                            lineHeight: "33.6px",
                            letterSpacing: "1%",
                            marginTop: "0.5rem",
                          }}
                        >
                          Selecione o lote
                        </Typography>
                      </label>
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        {isLoadingBatches ? (
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <Select
                            id="batchSelect"
                            value={selectedBatch}
                            onChange={handleBatchChange}
                            disabled={isLoading}
                            displayEmpty
                            style={{
                              fontSize: "18px",
                              width: "260px",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                              textAlign: "center",
                              height: "2.5rem",
                            }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 200,
                                  overflowY: "auto",
                                },
                              },
                            }}
                            renderValue={selected => {
                              if (!selected) {
                                return "Selecione..."
                              }
                              return selected
                            }}
                          >
                            {batches.length > 0 ? (
                              batches.map(batchId => (
                                <MenuItem key={batchId} value={batchId}>
                                  {batchId}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled>Nenhum lote encontrado, informe o PCP.</MenuItem>
                            )}
                          </Select>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="receivingMarketNumber"
                      style={{
                        display: "block",
                        fontSize: "18px",
                        fontWeight: "500",
                        marginBottom: "0.5rem",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: "500",
                          fontSize: "20px",
                          lineHeight: "33.6px",
                          letterSpacing: "1%",
                          marginTop: "0.5rem",
                        }}
                      >
                        Vai abastecer qual mercado?
                      </Typography>
                    </label>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <Select
                        id="receivingMarketNumber"
                        value={marketReceiving}
                        onChange={handleReceivingMarketNumberChange}
                        disabled={isLoading}
                        displayEmpty
                        style={{
                          fontSize: "18px",
                          width: "260px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          textAlign: "center",
                          height: "2.5rem",
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              overflowY: "auto",
                            },
                          },
                        }}
                        renderValue={selected => {
                          if (!selected) {
                            return "Selecione..."
                          }
                          return `Mercado ${selected}`
                        }}
                      >
                        {generateNumberList.map(number => (
                          <MenuItem key={number} value={number}>
                            Mercado {number}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <button
                    disabled={disableConfirmBotton}
                    onClick={handleCreateReplenishment}
                    style={{
                      marginTop: "0.5rem",
                      padding: "10px 20px",
                      fontSize: "18px",
                      backgroundColor: disableConfirmBotton ? "#FFC78F" : "#FF8C42",
                      cursor: disableConfirmBotton ? "not-allowed" : "pointer",
                      color: "white",
                      border: "none",
                      borderRadius: "16px",
                      width: "300px",
                    }}
                  >
                    Abastecer
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </Box>
  )
}

export default EspecificSkuReplenishing
